import { format, parse } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import type { UserConfig } from '@/slices/userConfigSlice';

export type DateFormattingConfig = {
  dateFormat: string;
  timezone: string;
};

// Detects ISO-like date strings (date or datetime)
const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
const isoDateTimePrefixRegex = /^\d{4}-\d{2}-\d{2}[T\s]/;

// Detects DDMonYY like 05Aug25 (no separator)
const ddMonYYRegex = /^\d{2}[A-Za-z]{3}\d{2}$/;

// Detects DD-Mon-YY like 05-Mar-26 (dash separator)
const ddDashMonDashYYRegex = /^\d{2}-[A-Za-z]{3}-\d{2}$/;

// Detects Mon-YY like Dec-25 (month-year, no day)
const monDashYYRegex = /^[A-Za-z]{3}-\d{2}$/;

const DEFAULT_DATE_FORMAT = 'dd-MMM-yy';
const DEFAULT_TIMEZONE = 'UTC';

type LegacyInputFormat = {
  format: string;
  isMonthYearOnly: boolean;
};

const isAsciiLetter = (char: string): boolean => {
  const code = char.charCodeAt(0);
  return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
};

const trimNonLetterEdges = (value: string): string => {
  let start = 0;
  let end = value.length;

  while (start < end && !isAsciiLetter(value[start])) start += 1;
  while (end > start && !isAsciiLetter(value[end - 1])) end -= 1;

  return value.slice(start, end);
};

const collapseRepeatedSeparators = (value: string): string => {
  if (value.length < 2) return value;

  let collapsed = value[0];
  for (let i = 1; i < value.length; i += 1) {
    const char = value[i];
    const previousChar = value[i - 1];
    const isRepeatedSeparator = !isAsciiLetter(char) && char === previousChar;
    if (!isRepeatedSeparator) {
      collapsed += char;
    }
  }

  return collapsed;
};

/**
 * Strips day tokens (`d`, `dd`) from a date-fns format string so month-year
 * values like "Dec-25" don't get an artificial day-1 prefix in the output.
 * E.g. "d/MM/yyyy" → "MM/yyyy", "dd-MMM-yy" → "MMM-yy".
 */
function stripDayFromFormat(dateFormat: string): string {
  const withoutDayTokens = dateFormat.replace(/(?<![A-Za-z])d{1,2}(?![A-Za-z])/g, '');
  const withoutEdgeSeparators = trimNonLetterEdges(withoutDayTokens);
  const normalizedSeparators = collapseRepeatedSeparators(withoutEdgeSeparators);
  return normalizedSeparators || 'MMM-yy';
}

export type ParsedDateResult = {
  formatted: string;
  date: Date | null;
  matched: boolean;
};

function toZoned(date: Date, timezone: string) {
  try {
    return toZonedTime(date, timezone);
  } catch {
    return date;
  }
}

function isUserConfig(userConfig: UserConfig | DateFormattingConfig): userConfig is UserConfig {
  return typeof userConfig.dateFormat === 'object';
}

function resolveFormattingConfig(
  userConfig: UserConfig | DateFormattingConfig
): DateFormattingConfig {
  if (isUserConfig(userConfig)) {
    return {
      dateFormat: userConfig.dateFormat.value || DEFAULT_DATE_FORMAT,
      timezone: userConfig.timezone.value || DEFAULT_TIMEZONE
    };
  }

  return {
    dateFormat: userConfig.dateFormat || DEFAULT_DATE_FORMAT,
    timezone: userConfig.timezone || DEFAULT_TIMEZONE
  };
}

function getLegacyInputFormat(value: string): LegacyInputFormat | null {
  if (ddMonYYRegex.test(value)) {
    return { format: 'ddMMMyy', isMonthYearOnly: false };
  }

  if (ddDashMonDashYYRegex.test(value)) {
    return { format: 'dd-MMM-yy', isMonthYearOnly: false };
  }

  if (monDashYYRegex.test(value)) {
    return { format: 'MMM-yy', isMonthYearOnly: true };
  }

  return null;
}

function parseIsoLikeValue(
  value: string,
  dateFormat: string,
  timezone: string
): ParsedDateResult | null {
  if (!isoDateRegex.test(value) && !isoDateTimePrefixRegex.test(value)) {
    return null;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const zonedDate = toZoned(parsedDate, timezone);
  return { formatted: format(zonedDate, dateFormat), date: zonedDate, matched: true };
}

function parseLegacyValue(
  value: string,
  dateFormat: string,
  timezone: string
): ParsedDateResult | null {
  const legacyInputFormat = getLegacyInputFormat(value);
  if (!legacyInputFormat) {
    return null;
  }

  // Parse with a fixed UTC-like baseline to avoid local timezone shifting the date
  const baseline = new Date(Date.UTC(2000, 0, 1, 12, 0, 0)); // midday UTC to avoid DST quirks
  const parsed = parse(value, legacyInputFormat.format, baseline);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  // Treat parsed as UTC date (date-only). Create a pure date using its Y/M/D in UTC
  const asUtcDate = new Date(
    Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 0, 0, 0)
  );
  const zonedDate = toZoned(asUtcDate, timezone);
  const outputFormat = legacyInputFormat.isMonthYearOnly
    ? stripDayFromFormat(dateFormat)
    : dateFormat;

  return { formatted: format(zonedDate, outputFormat), date: zonedDate, matched: true };
}

export function parseAndFormatUserDateString(
  value: string,
  userConfig: UserConfig | DateFormattingConfig
): ParsedDateResult {
  const { dateFormat, timezone } = resolveFormattingConfig(userConfig);

  const isoResult = parseIsoLikeValue(value, dateFormat, timezone);
  if (isoResult) return isoResult;

  const legacyResult = parseLegacyValue(value, dateFormat, timezone);
  if (legacyResult) return legacyResult;

  return { formatted: value, date: null, matched: false };
}
